import React from 'react';
import { useRouterParam } from '../../../model/history';
import { Container } from '../../../components/Container/Container';
import { useRemoteData } from '../../../model/data';
import { useMeta, getPageTitle } from '../../../model/meta';
import { getDeliveryWithOrder, Delivery } from '../../../model/delivery';
import { fulfillmentPath } from '../../../routes';
import { LoadingPage } from '../../../components/LoadingPage/LoadingPage';
import { FulfillmentOrderDetails } from './FulfillmentOrderDetails';
import { Order } from '../../../model/order';
import './FulfillmentDetails.css';
import { OrderProducts } from './OrderProducts';
import { t } from '../../../locales/translate';
import { Activities } from '../../../components/Activities/Activities';
import { Stack } from '../../../components/Stack/Stack';
import { Product, ProductInGroup } from '../../../model/product';
import { compact, uniqBy } from 'lodash';
import { ProductImportantActivities } from '../../../components/Activities/ProductImportantActivities';
import { highlightElements, smoothScrollToElement } from '../../../util/ui-helpers';

function highlightFulfillmentItems(sku?: string): void {
  if (sku) {
    smoothScrollToElement('fulfillment-items-anchor');
    highlightElements(`.pack-products__product-${sku}`);
  }
}

export function FulfillmentDetails(): JSX.Element {
  const id = useRouterParam<{ id: string }>().id;
  const [delivery, loading, error] = useRemoteData(
    () => getDeliveryWithOrder(id),
    (db) => db.table('deliveries').where({ id })[0],
    [id]
  );

  useMeta({
    title: getPageTitle(loading, delivery ? delivery.reference : '', delivery),
    breadcrumbs: [
      { title: 'Fulfillment', url: fulfillmentPath() },
      {
        title: getPageTitle(loading, delivery ? delivery.reference : '', delivery),
      },
    ],
  });

  if (loading) {
    return <LoadingPage title={t.actions.loading(t.model.order._name)} />;
  } else if (error || !delivery) {
    return <Container>{t.errors.itemNotFound(t.model.order._name)}.</Container>;
  }

  const productsInGroup: Array<ProductInGroup> = uniqBy(
    compact<Partial<Product> & { groupId: string }>(
      delivery.order?.itemGroups?.map((group) => ({ ...group.product, groupId: group.id }))
    ),
    'id'
  );

  return (
    <Container>
      <Stack size="lg">
        <FulfillmentOrderDetails delivery={delivery as Delivery & { order: Order }} />
        {delivery.order && (
          <>
            <ProductImportantActivities
              activitiesComponentClassName="fulfillment-activities"
              products={productsInGroup}
              onProductLinkClick={highlightFulfillmentItems}
            />
            <Activities
              className="fulfillment-activities"
              title={t.model.activity.comments}
              readOnly
              subjectType="order"
              subjectId={delivery.order.id}
              metaFilter="shipmentRelevant"
              collapseWhenLoading
              collapseWhenEmpty
            />
            <OrderProducts anchorId="fulfillment-items-anchor" delivery={delivery as Delivery & { order: Order }} />
          </>
        )}
      </Stack>
    </Container>
  );
}
